/**
 * Lectura de lo que codifica un QR de autenticación — puro, sin dependencias,
 * corre en el navegador (asistente de MFA del Vault) y en node:test.
 *
 *  - `otpauth://totp/Issuer:cuenta?secret=…&digits=6&period=30&algorithm=SHA1`
 *    (el QR de configuración que muestra cualquier plataforma)
 *  - `otpauth-migration://offline?data=<base64 protobuf>` (el QR de
 *    "Transferir cuentas → Exportar" de Google Authenticator; trae varias cuentas)
 *
 * El protobuf de la migración no tiene esquema público, pero es estable desde
 * 2020: MigrationPayload { repeated OtpParameters otp_parameters = 1; … } y
 * OtpParameters { bytes secret = 1; string name = 2; string issuer = 3;
 * int32 algorithm = 4 (1 SHA1, 2 SHA256, 3 SHA512, 4 MD5); int32 digits = 5
 * (1 = 6, 2 = 8); int32 type = 6 (1 HOTP, 2 TOTP); int64 counter = 7 }.
 */

import { base32Encode } from './base32';

export interface OtpAccount {
  issuer: string;
  name: string;
  /** Semilla en base32 canónico (mayúsculas, sin `=`), lista para el Vault. */
  secret: string;
  digits: number;
  algorithm: 'SHA1' | 'SHA256' | 'SHA512' | 'MD5';
  type: 'TOTP' | 'HOTP';
  period: number;
}

/** `corrupt`: era una URI de autenticación pero está cortada o dañada (pegado incompleto, captura ilegible). */
export type OtpQrKind = 'setup' | 'migration' | 'unknown' | 'corrupt';

export { base32Encode } from './base32';

function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/-/g, '+').replace(/_/g, '/').replace(/\s/g, '');
  const bin = typeof atob === 'function' ? atob(clean) : Buffer.from(clean, 'base64').toString('binary');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function utf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

function readVarint(b: Uint8Array, i: number): [bigint, number] {
  let result = 0n;
  let shift = 0n;
  for (;;) {
    if (i >= b.length) throw new Error('protobuf truncado');
    const x = b[i++];
    result |= BigInt(x & 0x7f) << shift;
    if (!(x & 0x80)) break;
    shift += 7n;
  }
  return [result, i];
}

/** Recorre un mensaje protobuf y llama `cb(field, value)` (varint como bigint, length-delimited como bytes). */
function walk(b: Uint8Array, cb: (field: number, value: bigint | Uint8Array) => void): void {
  let i = 0;
  while (i < b.length) {
    let key: bigint;
    [key, i] = readVarint(b, i);
    const field = Number(key >> 3n);
    const wire = Number(key & 7n);
    if (wire === 0) {
      let v: bigint;
      [v, i] = readVarint(b, i);
      cb(field, v);
    } else if (wire === 2) {
      let len: bigint;
      [len, i] = readVarint(b, i);
      const n = Number(len);
      if (i + n > b.length) throw new Error('protobuf truncado'); // subarray no tira: un pegado cortado pasaría como válido
      cb(field, b.subarray(i, i + n));
      i += n;
    } else if (wire === 1) {
      i += 8;
    } else if (wire === 5) {
      i += 4;
    } else {
      throw new Error(`protobuf: wire type ${wire} no soportado`);
    }
  }
}

const ALGORITHMS: Record<number, OtpAccount['algorithm']> = { 1: 'SHA1', 2: 'SHA256', 3: 'SHA512', 4: 'MD5' };

export function parseMigrationPayload(data: Uint8Array): OtpAccount[] {
  const accounts: OtpAccount[] = [];
  walk(data, (field, value) => {
    if (field !== 1 || !(value instanceof Uint8Array)) return;
    const a: OtpAccount = { issuer: '', name: '', secret: '', digits: 6, algorithm: 'SHA1', type: 'TOTP', period: 30 };
    walk(value, (f, v) => {
      if (f === 1 && v instanceof Uint8Array) a.secret = base32Encode(v);
      else if (f === 2 && v instanceof Uint8Array) a.name = utf8(v);
      else if (f === 3 && v instanceof Uint8Array) a.issuer = utf8(v);
      else if (f === 4 && typeof v === 'bigint') a.algorithm = ALGORITHMS[Number(v)] ?? 'SHA1';
      else if (f === 5 && typeof v === 'bigint') a.digits = Number(v) === 2 ? 8 : 6;
      else if (f === 6 && typeof v === 'bigint') a.type = Number(v) === 1 ? 'HOTP' : 'TOTP';
    });
    if (a.secret) accounts.push(a);
  });
  return accounts;
}

export function parseSetupUri(text: string): OtpAccount | null {
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== 'otpauth:') return null;
  const type = url.host.toLowerCase() === 'hotp' ? 'HOTP' : 'TOTP';
  const label = decodeURIComponent(url.pathname.replace(/^\/+/, ''));
  const [labelIssuer, labelName] = label.includes(':') ? [label.slice(0, label.indexOf(':')), label.slice(label.indexOf(':') + 1)] : ['', label];
  const secret = (url.searchParams.get('secret') ?? '').toUpperCase().replace(/[\s\-=]/g, '');
  if (!secret) return null;
  const alg = (url.searchParams.get('algorithm') ?? 'SHA1').toUpperCase();
  return {
    issuer: url.searchParams.get('issuer') ?? labelIssuer.trim(),
    name: labelName.trim(),
    secret,
    digits: Number(url.searchParams.get('digits') ?? 6) || 6,
    algorithm: alg === 'SHA256' || alg === 'SHA512' || alg === 'MD5' ? alg : 'SHA1',
    type,
    period: Number(url.searchParams.get('period') ?? 30) || 30,
  };
}

/** Qué clase de QR es, y las cuentas que trae (0, 1 o varias). */
export function parseOtpQr(text: string): { kind: OtpQrKind; accounts: OtpAccount[] } {
  const t = text.trim();
  try {
    if (/^otpauth-migration:\/\//i.test(t)) {
      const data = new URL(t).searchParams.get('data') ?? '';
      return { kind: 'migration', accounts: parseMigrationPayload(base64ToBytes(decodeURIComponent(data))) };
    }
    if (/^otpauth:\/\//i.test(t)) {
      const a = parseSetupUri(t);
      return { kind: 'setup', accounts: a ? [a] : [] };
    }
  } catch {
    // base64 inválido, protobuf truncado, URI rota: nunca tira, el asistente lo explica
    return { kind: 'corrupt', accounts: [] };
  }
  return { kind: 'unknown', accounts: [] };
}

/** Etiqueta legible para elegir entre varias cuentas exportadas. */
export function otpAccountLabel(a: OtpAccount): string {
  return [a.issuer, a.name].filter(Boolean).join(' · ') || '(sin nombre)';
}

/**
 * ¿El texto parece una clave TOTP o una URI otpauth? Sirve para dos cosas:
 * en el front, tratar lo pegado como clave (y no mandarlo al modelo); en el
 * backend, negarse a pasar ese texto a Bedrock. Cuenta como clave: una URI
 * otpauth; una palabra base32 de 16+ caracteres con algún dígito, en
 * mayúsculas, o de 20+ en cualquier caso (code-reviewer 28/09: una clave sin
 * dígitos 2-7 pasa ~4% de las veces, y "electrodomesticos" tiene 17 letras: el
 * falso positivo solo rebota una pregunta con "pegala en el campo de la clave");
 * o 4+ grupos seguidos de 4 caracteres base32 ("JBSW Y3DP …" / "jbsw y3dp …").
 */
export function looksLikeOtpSecret(text: string): boolean {
  if (/otpauth(-migration)?:\/\//i.test(text)) return true;
  const tokens = text.split(/\s+/).map((t) => t.replace(/-/g, ''));
  let groups = 0;
  for (const tok of tokens) {
    if (/^[A-Za-z2-7]{16,}$/.test(tok) && (/[2-7]/.test(tok) || tok === tok.toUpperCase() || tok.length >= 20)) return true;
    groups = /^[A-Za-z2-7]{4}$/.test(tok) && (tok === tok.toUpperCase() || tok === tok.toLowerCase()) ? groups + 1 : 0;
    if (groups >= 4) return true;
  }
  return false;
}

/**
 * Tapa lo que podría ser una clave antes de LOGUEAR un texto (telemetría de
 * Bedrock en BigQuery, de audiencia más amplia que la credencial): runs de 12+
 * caracteres base32 y grupos "xxxx xxxx xxxx", con o sin espacios/guiones.
 */
export function redactOtpSecrets(text: string): string {
  return text
    .replace(/otpauth(-migration)?:\/\/\S+/gi, '[uri]')
    .replace(/\b(?:[A-Za-z2-7]{4}[ -]){2,}[A-Za-z2-7]{4}\b/g, '[clave]')
    .replace(/\b[A-Za-z2-7]{12,}\b/g, (m) => (/[2-7]/.test(m) || m === m.toUpperCase() || m.length >= 20 ? '[clave]' : m));
}
